import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #renderingNext = false;

  /** @type {boolean} */
  #renderingPrevious = false;

  /** @type {WeakSet<Element>} */
  #observedSentinels = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    // CU-86e382cf9: the server renders only ?page=N, so a scroll position restored from the longer pages 1…N
    // document clamps to the bottom and auto-loads N+1 on every reload. Restore the visible card ourselves instead;
    // the #anchor still handles back-from-product.
    if (this.refs.viewMoreNext) {
      history.scrollRestoration = 'manual';
      window.addEventListener('scroll', this.#scheduleRecordVisibleCard, { passive: true });
      this.#restoreVisibleCard();
    }

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#fetchPage('next');
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    window.removeEventListener('scroll', this.#scheduleRecordVisibleCard);
    clearTimeout(this.#recordVisibleCardTimeout);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            // Use current refs to check which element triggered
            const { viewMorePrevious, viewMoreNext } = this.refs;

            // CU-86e382cf9: observe() always delivers an initial entry for the target. Skip it for
            // `viewMorePrevious` so previous pages only load on a real upward scroll, not on landing.
            if (entry.target === viewMorePrevious && !this.#observedSentinels.has(entry.target)) {
              this.#observedSentinels.add(entry.target);
              continue;
            }

            if (entry.isIntersecting) {
              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: '100px',
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {number} page
   * @returns {boolean} Whether the grid already contains cards for this page
   */
  #hasPage(page) {
    return Boolean(this.refs.grid?.querySelector(`:scope > [data-page="${page}"]`));
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    // CU-86e382cf9: the observer can call this more than once for the same page (duplicate entries,
    // stale refs). One render in flight per direction, and never insert a page already in the grid.
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    try {
      const nextPage = this.#getPage('next');

      if (!nextPage || !this.#shouldUsePage(nextPage) || this.#hasPage(nextPage.page)) return;
      let nextPageItemElements = this.#getGridForPage(nextPage.page);

      if (!nextPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolveNextPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('next');

        await promise;
        nextPageItemElements = this.#getGridForPage(nextPage.page);
        if (!nextPageItemElements || this.#hasPage(nextPage.page)) return;
      }

      grid.append(...nextPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // replaceState: infinite scroll must not add history entries
      history.replaceState('', '', nextPage.url.toString());

      requestIdleCallback(() => {
        this.#fetchPage('next');
      });
    } finally {
      this.#renderingNext = false;
    }
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    if (this.#renderingPrevious) return;
    this.#renderingPrevious = true;

    try {
      const previousPage = this.#getPage('previous');
      if (!previousPage || !this.#shouldUsePage(previousPage) || this.#hasPage(previousPage.page)) return;

      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage('previous');

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
        if (!previousPageItemElements || this.#hasPage(previousPage.page)) return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      // Prepend the new elements
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      // replaceState: infinite scroll must not add history entries
      history.replaceState('', '', previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      requestIdleCallback(() => {
        this.#fetchPage('previous');
      });
    } finally {
      this.#renderingPrevious = false;
    }
  }

  /** @type {number | undefined} */
  #recordVisibleCardTimeout;

  /**
   * CU-86e382cf9: record once scrolling pauses. pagehide is too late (Chrome reports history.state as null and
   * drops replaceState while unloading), and the debounce keeps replaceState under Safari's rate limit.
   */
  #scheduleRecordVisibleCard = () => {
    clearTimeout(this.#recordVisibleCardTimeout);
    this.#recordVisibleCardTimeout = setTimeout(this.#recordVisibleCard, 200);
  };

  /**
   * CU-86e382cf9: remember the first visible card and its viewport offset in this history entry, so a reload
   * can put it back (history.state survives a reload and stays out of the URL).
   */
  #recordVisibleCard = () => {
    const card = (this.refs.cards ?? []).find((cardElement) => cardElement.getBoundingClientRect().bottom > 0);
    if (!card) return;

    const reloadAnchor = {
      page: Number(card.dataset.page),
      productId: card.dataset.productId,
      offset: Math.round(card.getBoundingClientRect().top),
    };
    history.replaceState({ ...history.state, reloadAnchor }, '');
  };

  /**
   * CU-86e382cf9: on reload, load the pages between the recorded card and the server-rendered page,
   * then scroll the card back to its recorded viewport offset. Missing card → stay at the top of page N.
   */
  async #restoreVisibleCard() {
    const navigation = /** @type {PerformanceNavigationTiming | undefined} */ (
      performance.getEntriesByType('navigation')[0]
    );
    const reloadAnchor = history.state?.reloadAnchor;
    const { grid, cards } = this.refs;
    if (navigation?.type !== 'reload' || !reloadAnchor || !grid || !cards?.length) return;

    const renderedPages = cards.map((cardElement) => Number(cardElement.dataset.page));
    const firstPage = Math.min(...renderedPages);
    const lastPage = Math.max(...renderedPages);
    const pagesBefore = [];
    const pagesAfter = [];
    for (let pageNumber = reloadAnchor.page; pageNumber < firstPage; pageNumber++) pagesBefore.push(pageNumber);
    for (let pageNumber = lastPage + 1; pageNumber <= reloadAnchor.page; pageNumber++) pagesAfter.push(pageNumber);

    if (pagesBefore.length || pagesAfter.length) {
      await Promise.all([...pagesBefore, ...pagesAfter].map((pageNumber) => this.#fetchSpecificPage(pageNumber)));

      const insert = (/** @type {number} */ pageNumber, /** @type {'prepend' | 'append'} */ method) => {
        const pageItemElements = this.#getGridForPage(pageNumber);
        if (pageItemElements && !this.#hasPage(pageNumber)) grid[method](...pageItemElements);
      };
      // Prepend from the closest page outwards so earlier pages end up above.
      for (const pageNumber of [...pagesBefore].reverse()) insert(pageNumber, 'prepend');
      for (const pageNumber of pagesAfter) insert(pageNumber, 'append');
      this.#aspectRatioHelper?.processNewElements();
    }

    const card = grid.querySelector(
      `:scope > [data-page="${reloadAnchor.page}"][data-product-id="${reloadAnchor.productId}"]`
    );
    if (!card) return;

    // Scroll anchoring and late layout can move the card after the first scrollTo (measured −10 px when pages
    // were prepended), so re-check every frame for ~2 s and stop as soon as the visitor interacts.
    const interactionEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
    let framesLeft = 120;
    const stopAligning = () => {
      framesLeft = 0;
      for (const type of interactionEvents) window.removeEventListener(type, stopAligning);
    };
    for (const type of interactionEvents) window.addEventListener(type, stopAligning, { once: true, passive: true });

    const keepCardAligned = () => {
      if (framesLeft <= 0) return;
      const drift = card.getBoundingClientRect().top - reloadAnchor.offset;
      if (Math.abs(drift) >= 1) window.scrollBy({ top: drift, behavior: 'instant' });
      framesLeft -= 1;
      if (framesLeft > 0) requestAnimationFrame(keepCardAligned);
      else stopAligning();
    };
    keepCardAligned();
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.refs.grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.refs.grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next page
        this.#fetchPage('next');
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
